import React, {
  DependencyList,
  EffectCallback,
  useEffect,
  useRef,
} from "react";
import intersection from "lodash/intersection";
import uniq from "lodash/uniq";
import { IToastProps } from "@blueprintjs/core";
import * as Sentry from "@sentry/react";

import { ApiError } from "@api/fetch";
import { AppToaster } from "@components/AppToaster";
import {
  ALLOWED_VISUAL_PATTERN_KEYS,
  CLI_TOKEN_PARAM,
  LS_CLI_TOKEN,
} from "@constants";
import { CliMatch, OrgData, TimeLookbackRange } from "@types";
import { calculateLookbackSeconds } from "@utils/calculateLookbackSeconds";

import { SimpleRule } from "@pages/editor/types";
import { Rule } from "@pages/registry/types";

import { Feature } from "./feature";
import { Language } from "./lang";
import { isMac } from "./platformUtils";
import { recoverBrowserSecurityError } from "./storage";

export const appEnv =
  window.location.hostname === "semgrep.dev"
    ? "prod"
    : window.location.hostname === "staging.semgrep.dev"
    ? "staging"
    : window.location.hostname.endsWith("dev.semgrep.dev")
    ? "dev"
    : undefined;

export const mapStringToString = (
  map: { [key: string]: string },
  stringKey: string,
  defaultValue: string
): string => {
  return map[stringKey] || defaultValue;
};

export const makeParamsStr = (obj: { [key: string]: string }) =>
  Object.entries(obj)
    .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
    .join("&");

export const getSanitizedUrl = (unsanitizedUrl: string | undefined | null) => {
  let sanitizedUrl = undefined;
  try {
    // do not crash if unsanitizedUrl is a string, but not valid
    if (unsanitizedUrl && new URL(unsanitizedUrl).protocol.startsWith("http")) {
      // prevent XSS
      sanitizedUrl = unsanitizedUrl;
    }
  } catch (e) {
    console.error(
      `Provided repo_url '${unsanitizedUrl}' does not conform to http protocol, so links will not be displayed on page`
    );
  }
  return sanitizedUrl;
};

export const runShortcut = `${isMac() ? "⌘" : "Ctrl-"}⏎`;
export const saveShortcut = `${isMac() ? "⌘" : "Ctrl-"}S`;

export const BASE_GITHUB_URL = "https://github.com/";

export const CONFIG_DOC_LINK = "/docs/writing-rules/pattern-logic/";
export const PATTERN_DOC_LINK = "/docs/writing-rules/pattern-syntax/";

export const makeGhIssueUrl = (
  github_repo: string,
  title: string,
  body: string
): string => {
  return (
    github_repo +
    "/issues/new?title=" +
    encodeURIComponent(title) +
    "&body=" +
    encodeURIComponent(body)
  );
};

export const SEMGREP_REPO = "https://github.com/returntocorp/semgrep";

export const SEMGREP_ACTION_REPO =
  "https://github.com/returntocorp/semgrep-action";

export const SLACK_INVITE_URL =
  "https://join.slack.com/t/r2c-community/shared_invite/enQtNjU0NDYzMjAwODY4LWE3NTg1MGNhYTAwMzk5ZGRhMjQ2MzVhNGJiZjI1ZWQ0NjQ2YWI4ZGY3OGViMGJjNzA4ODQ3MjEzOWExNjZlNTA";

export const TWITTER_URL = "https://twitter.com/r2cdev";

export const DEFAULT_LANG = "python";

export const DEFAULT_PATTERN = `
rules:
- id: untitled_rule
  pattern: print("...")
  message: |
    Semgrep found a match
  severity: WARNING
`;

const DEFAULT_PATTERN_COMPONENT = {
  pattern: `print("...")`,
  type: "pattern" as ALLOWED_VISUAL_PATTERN_KEYS,
};

export const DEFAULT_RULE = (language: Language): SimpleRule => {
  // TODO: import these from semgrep_app/constants.py
  return {
    id: "untitled_rule",
    patterns: [DEFAULT_PATTERN_COMPONENT],
    languages: [language],
    message: "Semgrep found a match",
    severity: "WARNING",
    fix: "",
  };
};

const EMPTY_PATTERN_COMPONENT = {
  pattern: "",
  type: "pattern" as ALLOWED_VISUAL_PATTERN_KEYS,
};

export const EMPTY_RULE = (language: Language): SimpleRule => {
  // TODO: import these from semgrep_app/constants.py
  return {
    id: "untitled_rule",
    patterns: [EMPTY_PATTERN_COMPONENT],
    languages: [language],
    message: "Semgrep found a match",
    severity: "WARNING",
    fix: "",
  };
};

export const DEFAULT_RULE_VISIBILITY = {
  pattern: 1,
  message: true,
  fix: false,
};

export const DEFAULT_TARGET = `print("Welcome to Semgrep!" + "Use our Run button to start experimenting -->")


print("...")

# To detect ALL calls to the print() function, change the Semgrep Rule from print("...") to print(...)

print(not_a_string)

print(first_var, second_var)

print()

# print("This is commented out so it will never be found")
`;

export const EMPTY_TARGET = "";

export const makeDockerCmd = (id: string, isDevelop: boolean) =>
  `docker run --rm -v "\${PWD}:/src" returntocorp/semgrep:${
    isDevelop ? "develop" : "latest"
  } --config="${id}"`;

export const makeBinaryCmd = (id: string) => `semgrep --config="${id}"`;

// coupled to semgrep codebase
export const ERROR_TYPE_PATTERN = "Pattern parse error";
export const ERROR_TYPE_CODE = "Syntax error";

export const LANG_MONACO_MAP: { [key: string]: string } = {
  js: "javascript",
  ts: "typescript",
};

// default is `//` for most languages we support
export const LANG_COMMENT_SYNTAX_MAP: { [key: string]: string } = {
  python: "#",
  ruby: "#",
  lua: "--",
  r: "#",
};

export const makeResultKey = (r: CliMatch): string =>
  `${r.check_id}-${r.start.line}-${r.start.col}-${r.end.line}-${r.end.col}`;

export const makeDockerLink = (dockerImageHash: string): string => {
  return dockerImageHash.includes("sha526")
    ? `https://hub.docker.com/layers/returntocorp/semgrep/develop/images/${dockerImageHash.replace(
        ":",
        "-"
      )}`
    : `https://hub.docker.com/r/returntocorp/semgrep/tags?name=${dockerImageHash}`;
};

export const makeDockerShortLink = (dockerImageHash: string): string => {
  return (dockerImageHash.split(":").pop() || "").slice(0, 12);
};

/*
The curated and exhaustive lists of technologies, categories, etc.
Each list has a curated version (i.e. OWASP) and an exhausive version
(i.e. ALL_OWASP) where every rule falls into exactly one category
in the exclusive version. This means that each ALL list must have an
"other" label like "--no-owasp--" so that rules that are not in our
enumerated categories are still searchable and discoverable
*/
export const OWASP_2017 = [
  "injection",
  "authentication",
  "data-exposure",
  "xxe",
  "access",
  "misconfiguration",
  "xss",
  "deserialization",
  "vulnerable-components",
  "bad-logging",
];

export const OWASP_2021 = [
  "access",
  "crypto-failures",
  "injection",
  "insecure-design",
  "misconfiguration",
  "vulnerable-components",
  "authentication",
  "integrity",
  "bad-logging",
  "ssrf",
];
export const NO_OWASP = "--no-owasp--";
export const ALL_OWASP = uniq([NO_OWASP, ...OWASP_2017, ...OWASP_2021]);
export const TECHNOLOGIES = [
  "django",
  "docker",
  "dockerfile",
  "flask",
  "gorilla",
  "kubernetes",
  "nginx",
  "react",
  "terraform",
];
const NO_TECH = "--no-technology--";
export const ALL_TECHNOLOGIES = [NO_TECH, ...TECHNOLOGIES];
const CATEGORIES = [
  "best-practice",
  "correctness",
  "maintainability",
  "performance",
  "portability",
  "security",
]; // + dependencies (once we have rules for dependencies)
export const NO_CATEGORY = "--no-category--";
export const ALL_CATEGORIES = [NO_CATEGORY, ...CATEGORIES];

// assigns tags to each rule that cover categories, technologies, etc.
export const tagsFromRule = (rule: Rule): string[] => {
  const { path } = rule;
  if (path === undefined) return [];
  const parts = path.split(".");
  if (intersection([...OWASP_2017, ...OWASP_2021], parts).length === 0) {
    parts.push(NO_OWASP);
  }
  if (intersection(TECHNOLOGIES, parts).length === 0) {
    parts.push(NO_TECH);
  }
  if (intersection(CATEGORIES, parts).length === 0) {
    parts.push(NO_CATEGORY);
  }
  return parts;
};

/*******************************/

// adapted from https://github.com/samanmohamadi/use-debounced-effect
export const useDebouncedEffect = (
  callback: EffectCallback,
  delay: number,
  deps?: DependencyList
): void => {
  const firstUpdate = useRef(true);
  const depsArg = deps !== undefined ? deps.map((i) => i) : undefined;
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const handler = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [callback, delay, depsArg]);
};

export const dedent = (lines: string[]): string[] => {
  /* Dedent everything by what it takes to square the first line. */
  const firstLine: string = lines[0].replace(/(\s|\t)+/, "");
  const nDedents: number = lines[0].length - firstLine.length;
  const dedentedLines = lines.map((line: string): string => {
    var dedentedLine = line;
    dedentedLine = dedentedLine.replace(
      new RegExp(`(\\s|\\t){${nDedents}}`),
      ""
    ); // /(\s|\t){4}/
    return dedentedLine;
  });
  return dedentedLines;
};

function isApiError(error: any): error is ApiError {
  return (error as ApiError).body !== undefined;
}

export function withToaster<A, B>(
  f: (...args: A[]) => Promise<B>,
  errorToMessage: (err: ApiError) => React.ReactNode,
  toastProps?: Omit<IToastProps, "message" | "intent">
) {
  return (...args: A[]) => {
    const promise = f(...args);
    promise.catch((err) => {
      const message = isApiError(err)
        ? errorToMessage(err)
        : err.statusCode
        ? `Operation failed with response code: ${err.statusCode}`
        : JSON.stringify(err);
      console.error(err);
      Sentry.captureException(err);
      AppToaster.show({
        ...(toastProps ?? {}),
        message,
        intent: "danger",
      });
    });
    return promise;
  };
}

export const makeShareUrl = (id: string) =>
  `${window.location.origin}/s/${id}` + window.location.search;

export const SECOND = 1000; // milliseconds
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;
const NUMBER_OF_DAYS_TO_DISPLAY_COMMUNITY = 30;
const NUMBER_OF_DAYS_TO_DISPLAY_TEAM = 365;

export const number_of_days_to_display = (org: OrgData | undefined) => {
  if (org && Feature.hasTeamsTier({ org })) {
    return NUMBER_OF_DAYS_TO_DISPLAY_TEAM;
  }
  return NUMBER_OF_DAYS_TO_DISPLAY_COMMUNITY;
};

export const ago = (org: OrgData | undefined) => {
  const num_days = number_of_days_to_display(org);
  return num_days * DAY;
};

export const getColorForPercentage = (pct: number) => {
  const percentColors = [
    { pct: 0.0, color: { r: 0xff, g: 0x00, b: 0 } },
    { pct: 0.5, color: { r: 0xff, g: 0xff, b: 0 } },
    { pct: 1.0, color: { r: 0x00, g: 0xff, b: 0 } },
  ];

  for (var i = 1; i < percentColors.length - 1; i++) {
    if (pct < percentColors[i].pct) {
      break;
    }
  }

  const lower = percentColors[i - 1];
  const upper = percentColors[i];
  const range = upper.pct - lower.pct;
  const rangePct = (pct - lower.pct) / range;
  const pctLower = 1 - rangePct;
  const pctUpper = rangePct;
  const color = {
    r: Math.floor(lower.color.r * pctLower + upper.color.r * pctUpper),
    g: Math.floor(lower.color.g * pctLower + upper.color.g * pctUpper),
    b: Math.floor(lower.color.b * pctLower + upper.color.b * pctUpper),
  };
  const opacity = 0.4;
  return "rgb(" + [color.r, color.g, color.b, opacity].join(",") + ")";
};

/** A function that does nothing */
export const noOp = () => {};

export const PromiseUtil = {
  /** A promise that never completes */
  never: <T>() => new Promise<T>(noOp),
};

export const changeTabId = (
  tabId: string,
  selectedTabId: string | null,
  defaultTab: string,
  search: URLSearchParams,
  setSearch: (params: URLSearchParams) => void
) => {
  if (tabId === selectedTabId) return;
  const params = new URLSearchParams(search);
  if (tabId === defaultTab) {
    params.delete("tab");
  } else {
    params.set("tab", tabId);
  }
  // With changing the tab, we want to reset the page to 1
  params.delete("page");
  setSearch(params);
};

/** Converts a character offset in a text document to a line / column pair */
export const offsetToPosition = (
  doc: string,
  offset: number
): { line: number; col: number } => {
  // Good lord I can't believe there's not a library for this :(
  let line = 1;
  let col = 1;
  for (let i = 0; i < offset; i++) {
    if (doc[i] === "\n") {
      line += 1;
      col = 1;
    } else {
      col += 1;
    }
  }
  return { line, col };
};

export const abbreviateNumber = (inputNumber: number): string => {
  // Utility function to convert numbers into "pretty printable" strings
  // Using .toFixed makes it round up a bit sometimes, but that's fine.
  if (inputNumber < 1000) {
    return inputNumber.toString(); // Under a 1000, don't do anything
  }
  if (inputNumber < 100000) {
    return Number((inputNumber / 1000).toFixed(1)).toString() + "k"; // Example: 32323 -> 32.3k
  }
  return "100k+"; // Lets keep it simple here, for the corner cases (easy to extend)
};

/**
 * Util functions for optimistic updates to react-query mutations
 */

/**
 *
 * @param oldItems List of items that will be updated
 * @param newItem Item that has been created
 * @returns List of items with newItem added
 */
export function optimisticCreate<T>(oldItems: T[] | undefined, newItem: T) {
  return oldItems ? [...oldItems, newItem] : [newItem];
}

/**
 *
 * @param oldItems List of items that will be updated
 * @param updatedItem Item that has been updated
 * @param key Field by which items in this list are identified. Ex: "id"
 * @returns List of items with old item replaced by updatedItem
 */
export function optimisticUpdate<T>(
  oldItems: T[] | undefined,
  updatedItem: T,
  key: keyof T
): T[] {
  if (oldItems === undefined) return [];

  const oldIndex = oldItems.findIndex((i) => i[key] === updatedItem[key]);
  let newItems = [...oldItems];

  if (oldIndex !== -1) {
    newItems[oldIndex] = updatedItem;
  }

  return newItems;
}

/**
 *
 * @param oldItems List of items that will be updated
 * @param deletedItem Item that has been deleted
 * @param key Field by which items in this list are identified. Ex: "id"
 * @returns List of items with deletedItem removed
 */
export function optimisticDelete<T>(
  oldItems: T[] | undefined,
  deletedItem: T,
  key: keyof T
) {
  if (oldItems === undefined) return [];

  const oldActionIndex = oldItems.findIndex((i) => i[key] === deletedItem[key]);
  let newItems = [...oldItems];

  if (oldActionIndex !== -1) {
    newItems.splice(oldActionIndex, 1);
  }

  return newItems;
}

/**
 * Promise-based replacement for the async `confirm()`.
 *
 * @param message Message in the confirm prompt, answered with OK or Cancel
 * @returns True on confirmation, rejects with false otherwise
 */
export function confirmPrompt(message: string) {
  return new Promise(function (resolve, reject) {
    let isConfirmed = window.confirm(message);

    return isConfirmed ? resolve(true) : reject(false);
  });
}

export const get_google_public_oauth_url = () => {
  // Not secret information, these are url ID keys for SemgrepApp web
  switch (appEnv) {
    case "prod":
      return "646011448282-sed1rdkb8e33p5r3anro84gutudfdqt2.apps.googleusercontent.com"; // nosem: generic.secrets.security.detected-google-oauth.detected-google-oauth-url
    case "staging":
      return "1063492050660-731rt60i4deo3pad4q42gqp9ogpqggbm.apps.googleusercontent.com"; // nosem: generic.secrets.security.detected-google-oauth.detected-google-oauth-url
    default:
      // dev and localhost all use the dev server, so ok to group together here
      return "735995633259-dpiksu675t1t0f9de7c2f81e3j0msbr6.apps.googleusercontent.com"; // nosem: generic.secrets.security.detected-google-oauth.detected-google-oauth-url
  }
};

export const timeLookbackRangeToSince = (
  timeLookbackRange: TimeLookbackRange
): number => {
  const trueSince = calculateLookbackSeconds(timeLookbackRange);
  // To prevent duplicated loading, decrease precision to be 100 seconds
  const roundedSince = Math.floor(trueSince / 100) * 100;

  return roundedSince;
};

// validate URL to protect against XSS
export const validateUrl = (url: string | undefined) => {
  if (url === undefined) return undefined;
  const parsedUrl = new URL(url);
  if (parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:") {
    return url;
  } else {
    return "https://semgrep.dev";
  }
};

export const getCLILoginToken = () => {
  const CliTokenStorage = recoverBrowserSecurityError(() =>
    localStorage.getItem(LS_CLI_TOKEN)
  );
  if (CliTokenStorage) return CliTokenStorage;
  const params = new URLSearchParams(window.location.search);
  const CliTokenParam = params.get(CLI_TOKEN_PARAM);

  if (!CliTokenParam) return;

  recoverBrowserSecurityError(() =>
    localStorage.setItem(LS_CLI_TOKEN, CliTokenParam)
  );
  return CliTokenParam;
};
